import { MAX_AZURE_DEPLOYMENT_ID_LENGTH, azureDeploymentIdProblem, } from '../../deployment/deployment-id.js';
import { STATE_VERSION_RETENTION_DAYS } from '../template.js';
import { BUILT_IN_ROLES, RESOURCE_GROUP_SCHEMA, SUBSCRIPTION_SCHEMA, builtInRole, qrveyTags, } from './arm.js';
/**
 * One Azure instance's resource group, its Terraform state, and the deploy
 * app's rights over it (IM-45). Deployed once per instance, at subscription
 * scope, before the instance's first run.
 *
 * ## Why per instance
 *
 * The installer reads one resource group as both the state's and the
 * deployment's (`account_config.resource_group_name`, installer contract), and
 * reads it with a data source, so it must already exist. State therefore lives
 * beside the instance it describes, not in a shared per-region backend as on
 * AWS, and the group has to be made by someone before the installer runs.
 *
 * ## What this buys that AWS needed a policy set for
 *
 * The deploy app's rights are role assignments **on this group**, so a run
 * reaches the instance groups this template has made and nothing else in the
 * subscription — the customer's own resources are outside every scope it holds.
 * That is the property IM-50's session tags give a scoped AWS deploy role, by
 * construction rather than by condition. It is not per instance: one deploy
 * app holds every instance group in the subscription, so a run for one
 * instance could reach another's. Narrowing that needs one deploy app per
 * instance, which is more secrets for the customer to mint than it is worth
 * today.
 *
 * ## The three roles, and why each
 *
 * - **Contributor**: everything the installer creates. It cannot change access
 *   control, so it cannot remove the state account's lock or grant itself more.
 * - **Storage Blob Data Contributor**: Terraform state over Entra (the state
 *   account refuses shared keys), and the installer's destroy, which empties
 *   containers with `--auth-mode login`.
 * - **Role Based Access Control Administrator, held to two roles**: the
 *   installer assigns Network Contributor to AKS on its subnet and Storage
 *   Blob Data Contributor to its workload identities (`manage_role_assignments`).
 *   The condition lets it assign those two and nothing else, so it can never
 *   assign Owner, or itself anything new. The counterpart of the AWS
 *   `iam:PolicyARN` limit on `AttachRolePolicy`.
 *
 * Plus a custom role at subscription scope for the few reads the installer
 * makes there: quota, VM sizes and AKS versions. No resource's contents.
 */
/** Bumped whenever the template changes what it deploys. */
export const AZURE_INSTANCE_VERSION = '1';
/**
 * Why an id cannot be prefilled into an instance template, or undefined.
 *
 * IM-47's `azureDeploymentIdProblem`, plus one thing it rightly allows: a
 * blank id, or one with space around it, which a run trims and then mints or
 * reads. Here the id goes verbatim into a file name and a parameter default,
 * so there is nothing to trim it into.
 */
export const instanceArtifactIdProblem = (id) => id.trim() !== id || id === ''
    ? 'deployment_id may contain only lowercase letters and digits.'
    : azureDeploymentIdProblem(id);
/** The container Terraform's state goes in. */
export const STATE_CONTAINER_NAME = 'tfstate';
/** The roles the installer assigns, and so the only ones the deploy app may. */
export const ASSIGNABLE_BY_INSTALLER = [
    BUILT_IN_ROLES.networkContributor,
    BUILT_IN_ROLES.storageBlobDataContributor,
];
/**
 * The condition on the deploy app's role-assignment rights: it may create or
 * delete an assignment only of `ASSIGNABLE_BY_INSTALLER`. Azure's documented
 * form for constraining a delegated role-assignment administrator.
 */
export const ROLE_ASSIGNMENT_CONDITION = (() => {
    const roles = ASSIGNABLE_BY_INSTALLER.join(', ');
    return [
        "((!(ActionMatches{'Microsoft.Authorization/roleAssignments/write'})) OR ",
        `(@Request[Microsoft.Authorization/roleAssignments:RoleDefinitionId] ForAnyOfAnyValues:GuidEquals {${roles}}))`,
        ' AND ',
        "((!(ActionMatches{'Microsoft.Authorization/roleAssignments/delete'})) OR ",
        `(@Resource[Microsoft.Authorization/roleAssignments:RoleDefinitionId] ForAnyOfAnyValues:GuidEquals {${roles}}))`,
    ].join('');
})();
/**
 * What the installer reads at subscription scope, and nothing more.
 *
 * Read from the image, not measured: the quota preflight (`az vm list-usage`),
 * AKS's version step and the provider's subscription read. A run that needs
 * another will say so in its log, and the measurement belongs with IM-46's
 * first live apply.
 */
export const SUBSCRIPTION_READS = [
    'Microsoft.Resources/subscriptions/read',
    'Microsoft.Compute/locations/usages/read',
    'Microsoft.Compute/skus/read',
    'Microsoft.ContainerService/locations/kubernetesVersions/read',
];
const group = "[parameters('resourceGroupName')]";
const storage = "[toLower(format('{0}{1}', take(replace(parameters('deploymentId'), '-', ''), 11), uniqueString(subscription().id, parameters('deploymentId'))))]";
const subscriptionRole = "[subscriptionResourceId('Microsoft.Authorization/roleDefinitions', guid(subscription().id, 'qrvey-deploy-subscription-reads'))]";
const tags = {
    ...qrveyTags('instance', AZURE_INSTANCE_VERSION),
    'qrvey-id': "[parameters('deploymentId')]",
};
const groupAssignment = (role, condition) => ({
    type: 'Microsoft.Authorization/roleAssignments',
    apiVersion: '2022-04-01',
    name: `[guid(resourceGroup().id, parameters('deployPrincipalId'), '${role}')]`,
    properties: {
        roleDefinitionId: builtInRole(role),
        principalId: "[parameters('deployPrincipalId')]",
        principalType: 'ServicePrincipal',
        ...(condition === undefined ? {} : { condition, conditionVersion: '2.0' }),
    },
});
/** The group's contents, deployed into it by the nested deployment. */
const inGroup = {
    $schema: RESOURCE_GROUP_SCHEMA,
    contentVersion: '1.0.0.0',
    parameters: {
        deploymentId: { type: 'string' },
        deployPrincipalId: { type: 'string' },
        createStateStorage: { type: 'bool' },
        storageAccountName: { type: 'string' },
        tags: { type: 'object' },
    },
    resources: [
        {
            type: 'Microsoft.Storage/storageAccounts',
            apiVersion: '2023-05-01',
            name: "[parameters('storageAccountName')]",
            condition: "[parameters('createStateStorage')]",
            location: '[resourceGroup().location]',
            tags: "[union(parameters('tags'), createObject('qrvey-purpose', 'terraform-state'))]",
            // Locally redundant, because zone-redundant storage is not offered in
            // every region and this must deploy wherever the instance goes. Versions
            // are what recover a bad write; replication does not.
            sku: { name: 'Standard_LRS' },
            kind: 'StorageV2',
            properties: {
                minimumTlsVersion: 'TLS1_2',
                supportsHttpsTrafficOnly: true,
                allowBlobPublicAccess: false,
                // A state file holds every secret Terraform touched. Entra only: an
                // account key would let anything that can list keys read it.
                allowSharedKeyAccess: false,
                defaultToOAuthAuthentication: true,
                allowCrossTenantReplication: false,
            },
        },
        {
            type: 'Microsoft.Storage/storageAccounts/blobServices',
            apiVersion: '2023-05-01',
            name: "[format('{0}/default', parameters('storageAccountName'))]",
            condition: "[parameters('createStateStorage')]",
            dependsOn: [
                "[resourceId('Microsoft.Storage/storageAccounts', parameters('storageAccountName'))]",
            ],
            properties: {
                // Versioned, because a bad write to state is otherwise unrecoverable.
                isVersioningEnabled: true,
                deleteRetentionPolicy: { enabled: true, days: 30 },
                containerDeleteRetentionPolicy: { enabled: true, days: 30 },
            },
        },
        {
            type: 'Microsoft.Storage/storageAccounts/blobServices/containers',
            apiVersion: '2023-05-01',
            name: `[format('{0}/default/${STATE_CONTAINER_NAME}', parameters('storageAccountName'))]`,
            condition: "[parameters('createStateStorage')]",
            dependsOn: [
                "[resourceId('Microsoft.Storage/storageAccounts/blobServices', parameters('storageAccountName'), 'default')]",
            ],
            properties: { publicAccess: 'None' },
        },
        {
            // Old versions expire, as the AWS state bucket's noncurrent versions do.
            type: 'Microsoft.Storage/storageAccounts/managementPolicies',
            apiVersion: '2023-05-01',
            name: "[format('{0}/default', parameters('storageAccountName'))]",
            condition: "[parameters('createStateStorage')]",
            dependsOn: [
                "[resourceId('Microsoft.Storage/storageAccounts/blobServices', parameters('storageAccountName'), 'default')]",
            ],
            properties: {
                policy: {
                    rules: [
                        {
                            name: 'expire-old-state-versions',
                            enabled: true,
                            type: 'Lifecycle',
                            definition: {
                                filters: { blobTypes: ['blockBlob'] },
                                actions: {
                                    version: {
                                        delete: { daysAfterCreationGreaterThan: STATE_VERSION_RETENTION_DAYS },
                                    },
                                },
                            },
                        },
                    ],
                },
            },
        },
        {
            /*
             * The AWS state bucket's `Retain`. Deleting this account orphans the
             * instance: Terraform no longer knows what it built. Contributor cannot
             * remove a lock, so neither can a run; the customer can, deliberately.
             */
            type: 'Microsoft.Authorization/locks',
            apiVersion: '2020-05-01',
            name: 'qrvey-keep-terraform-state',
            condition: "[parameters('createStateStorage')]",
            scope: "[format('Microsoft.Storage/storageAccounts/{0}', parameters('storageAccountName'))]",
            dependsOn: [
                "[resourceId('Microsoft.Storage/storageAccounts', parameters('storageAccountName'))]",
            ],
            properties: {
                level: 'CanNotDelete',
                notes: 'Holds this Qrvey instance’s Terraform state. Deleting it orphans everything the instance created.',
            },
        },
        groupAssignment(BUILT_IN_ROLES.contributor),
        groupAssignment(BUILT_IN_ROLES.storageBlobDataContributor),
        groupAssignment(BUILT_IN_ROLES.roleBasedAccessControlAdministrator, ROLE_ASSIGNMENT_CONDITION),
    ],
};
export const buildInstanceTemplate = (input = {}) => ({
    $schema: SUBSCRIPTION_SCHEMA,
    contentVersion: '1.0.0.0',
    metadata: {
        description: `Qrvey instance (instance v${AZURE_INSTANCE_VERSION}). One per instance, before its first run.`,
    },
    parameters: {
        deploymentId: {
            type: 'string',
            minLength: 1,
            // The Azure installer's own cap (installer contract).
            maxLength: MAX_AZURE_DEPLOYMENT_ID_LENGTH,
            ...(input.deploymentId === undefined ? {} : { defaultValue: input.deploymentId }),
            metadata: { description: 'The instance id: lower-case letters and digits.' },
        },
        location: {
            type: 'string',
            defaultValue: '[deployment().location]',
            metadata: { description: 'Where the instance and its state live.' },
        },
        resourceGroupName: {
            type: 'string',
            defaultValue: "[format('qrvey-{0}', parameters('deploymentId'))]",
            metadata: { description: 'The instance’s resource group, created here if it is new.' },
        },
        deployPrincipalId: {
            type: 'string',
            minLength: 36,
            maxLength: 36,
            metadata: {
                description: 'The object id of the qrvey-deploy service principal, printed by the deploy-access script.',
            },
        },
        createStateStorage: {
            type: 'bool',
            defaultValue: true,
            metadata: {
                description: 'False for an instance whose state already lives in this group, such as an adopted one.',
            },
        },
    },
    variables: { storageAccountName: storage },
    resources: [
        {
            type: 'Microsoft.Resources/resourceGroups',
            apiVersion: '2022-09-01',
            name: group,
            location: "[parameters('location')]",
            tags,
        },
        {
            type: 'Microsoft.Authorization/roleDefinitions',
            apiVersion: '2022-04-01',
            name: "[guid(subscription().id, 'qrvey-deploy-subscription-reads')]",
            properties: {
                // Unique across the tenant, so it names its subscription.
                roleName: "[format('Qrvey deploy subscription reads ({0})', uniqueString(subscription().id))]",
                description: 'Quota, VM size and Kubernetes version reads the Qrvey installer makes. No resource contents.',
                type: 'CustomRole',
                permissions: [
                    { actions: [...SUBSCRIPTION_READS], notActions: [], dataActions: [], notDataActions: [] },
                ],
                assignableScopes: ['[subscription().id]'],
            },
        },
        {
            type: 'Microsoft.Authorization/roleAssignments',
            apiVersion: '2022-04-01',
            name: "[guid(subscription().id, parameters('deployPrincipalId'), 'qrvey-deploy-subscription-reads')]",
            dependsOn: [subscriptionRole],
            properties: {
                roleDefinitionId: subscriptionRole,
                principalId: "[parameters('deployPrincipalId')]",
                principalType: 'ServicePrincipal',
            },
        },
        {
            type: 'Microsoft.Resources/deployments',
            apiVersion: '2022-09-01',
            name: "[format('qrvey-instance-{0}', parameters('deploymentId'))]",
            resourceGroup: group,
            dependsOn: [`[resourceId('Microsoft.Resources/resourceGroups', ${group.slice(1, -1)})]`],
            properties: {
                mode: 'Incremental',
                expressionEvaluationOptions: { scope: 'inner' },
                parameters: {
                    deploymentId: { value: "[parameters('deploymentId')]" },
                    deployPrincipalId: { value: "[parameters('deployPrincipalId')]" },
                    createStateStorage: { value: "[parameters('createStateStorage')]" },
                    storageAccountName: { value: "[variables('storageAccountName')]" },
                    tags: { value: tags },
                },
                template: inGroup,
            },
        },
    ],
    /*
     * What the installer's `account_config` needs, as IM-47 builds it. Read
     * from this deployment by Qrvey's Reader, or from the storage account's
     * `qrvey-purpose` tag, never typed in.
     */
    outputs: {
        InstanceVersion: { type: 'string', value: AZURE_INSTANCE_VERSION },
        ResourceGroupName: { type: 'string', value: group },
        StateStorageAccountName: { type: 'string', value: "[variables('storageAccountName')]" },
        StateContainerName: { type: 'string', value: STATE_CONTAINER_NAME },
        StateKey: {
            type: 'string',
            value: "[format('{0}/state.tfstate', parameters('deploymentId'))]",
        },
    },
});
//# sourceMappingURL=instance-template.js.map